import React, { FC, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { CinemographHeroProps, CinemographHeroModel } from './CinemographHero.types';
import { StyledCinemographHero } from './CinemographHero.styles';
import { Image } from '@marriott/mi-ui-library';
import { debounce, useDCADynamicModelAlloy } from '../../modules/helpers';

interface PlayerInstance {
  /** Video.js player root; used to set attributes on the underlying `<video>`. */
  el?: () => Element;
  el_?: Element;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ready?: (callback: () => void) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getChild?: (name: string) => any;
}

const ReactPlayerLoader = dynamic(() => import('@brightcove/react-player-loader'), {
  ssr: false,
});

type LoaderSuccess = { ref?: PlayerInstance };

export const CinemographHero: FC<CinemographHeroProps> = props => {
  const [viewPort, setViewPort] = useState('');
  const [videoLoading, setVideoLoading] = useState(true);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    /**
     * debouncing handle resize event as it trigger's on every subsequent re size.
     * function will be executed only after the user has stopped resizing for a 300ms delay
     */
    const debouncedResize = debounce(handleResize, 300);
    handleResize();
    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  const handleResize = () => {
    const xs = window.matchMedia('(max-width: 767px)');
    if (xs?.matches) {
      setViewPort('Mobile');
    } else {
      setViewPort('viewPortM');
    }
  };

  const mboxParam = props?.model?.mboxParameter;
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  const resolvedModel: CinemographHeroModel = useMemo(
    () => (targetData ? targetData : props?.model),
    [targetData, props?.model]
  );

  const BRIGHTCOVE_PLAYER_OPTIONS = {
    // Native mobile HLS does not expose quality levels; VHS/MSE is required to populate the menu.
    html5: {
      vhs: {
        overrideNative: true,
      },
      nativeAudioTracks: false,
      nativeVideoTracks: false,
      nativeTextTracks: false,
    },
    responsive: true,
    autoplay: true,
    muted: true,
    aspectRatio: '16:9',
    loop: true,
    preload: 'auto',
    src: 'https://players.brightcove.net',
    userActions: {
      doubleClick: false,
    },
  };

  const variation = resolvedModel?.assetProperties?.variation ?? 'short';
  const videoId = useMemo(() => {
    const ap = resolvedModel?.assetProperties;
    if (!ap) return '';
    if (viewPort === 'Mobile') {
      return (ap?.brightcoveMobileVideoId ?? ap?.brightcoveVideoId) as string;
    }
    return ap?.brightcoveVideoId as string;
  }, [resolvedModel?.assetProperties, viewPort]);

  const imageRenditions = useMemo(() => {
    if (!resolvedModel?.dynamicImage) return undefined;
    if (viewPort !== 'Mobile') return resolvedModel?.dynamicImage?.renditions;
    return resolvedModel?.dynamicMobileImage?.renditions ?? resolvedModel?.dynamicImage?.renditions;
  }, [resolvedModel, viewPort]);

  const attachAnalyticsAttr = useCallback((success: LoaderSuccess) => {
    const { ref } = success;
    if (!ref) return;
    ref.el?.()?.querySelector('video')?.setAttribute('fetchpriority', 'high');
    ref.ready?.(() => {
      const controlBar = ref.getChild?.('ControlBar');
      if (controlBar && !controlBar.getChild?.('FullscreenToggle')) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (controlBar as any).addChild?.('FullscreenToggle', {}, controlBar.children_?.length);
      }

      const directionValue = document?.documentElement?.getAttribute('dir');
      const playerRoot = ref.el_ ?? ref.el?.();
      const videoControllers = playerRoot?.querySelector?.('.vjs-control-bar');
      if (directionValue === 'rtl' && videoControllers) {
        // Mirror icons only — never dir="rtl" on bar (breaks progress tooltip on AR)
        videoControllers.classList.add('cinemograph-vjs-control-bar--rtl');

        const applyArProgressLtr = () => {
          const progressControl = playerRoot?.querySelector?.('.vjs-progress-control') as HTMLElement | null;
          const progressHolder = playerRoot?.querySelector?.('.vjs-progress-holder') as HTMLElement | null;

          [progressControl, progressHolder].forEach(el => {
            el?.setAttribute('dir', 'ltr');
            if (el) el.style.direction = 'ltr';
          });

          if (progressControl) {
            progressControl.style.position = 'absolute';
            progressControl.style.left = '0';
            progressControl.style.right = '0';
            progressControl.style.width = '100%';
            progressControl.style.top = '-4.19px';
            progressControl.style.height = '4.19px';
          }
        };

        applyArProgressLtr();
        requestAnimationFrame(applyArProgressLtr);
      }
    });
    requestAnimationFrame(() => {
      if (isMountedRef.current) {
        setVideoLoading(false);
      }
    });
  }, []);

  return (
    <StyledCinemographHero className="m-container-fullbleed">
      <div
        className={clsx(
          resolvedModel?.assetProperties?.videoAccountId
            ? `loader-cinemograph loader-cinemograph-${variation} color-scheme2`
            : '',
          resolvedModel?.dynamicImage ? 'loader-cinemograph loader-cinemograph-image' : ''
        )}
      >
        <span
          className={clsx(
            'brand-logo-luxury-Mark cinemograph-loading-logo',
            videoLoading ? 'noanimation' : 'animate-logo',
            resolvedModel?.assetProperties?.videoAccountId ? '' : 'd-none'
          )}
        ></span>
        <span
          className={clsx(
            videoLoading ? 'sr-only' : 'd-none',
            resolvedModel?.assetProperties?.videoAccountId ? '' : 'd-none'
          )}
        >
          brand-logo-luxury-Mark
        </span>
        {resolvedModel?.activateOverlay && (
          <span className="row overlay_text p-1">
            <span className="icon-location t-label-xs mr-1" />
            <a href={resolvedModel?.overlayPath} className="t-label-xs">
              {resolvedModel?.overlayTitle}
            </a>
          </span>
        )}
        {resolvedModel?.dynamicImage && viewPort !== '' && (
          <Image
            title=""
            altText={resolvedModel?.dynamicImage?.altText}
            defaultImageURL={resolvedModel?.dynamicImage?.assetPath}
            renditions={imageRenditions}
            customClass={clsx(`cinemograph-image-${variation}`)}
            loading="eager"
            fetchpriority="high"
          />
        )}

        {!resolvedModel?.dynamicImage && resolvedModel?.assetProperties?.videoAccountId && viewPort !== '' && (
          <div
            className={clsx(
              'video-container',
              resolvedModel?.overlapNavigation && 'video-overlay',
              `cinemograph-${variation}`
            )}
          >
            <ReactPlayerLoader
              id={`luxury-cinemograph-${resolvedModel?.assetProperties?.videoAccountId}`}
              accountId={resolvedModel?.assetProperties?.videoAccountId as string}
              options={BRIGHTCOVE_PLAYER_OPTIONS}
              videoId={videoId}
              playerId={resolvedModel?.assetProperties?.playerId ?? 'default'}
              onSuccess={attachAnalyticsAttr}
            />
            {resolvedModel?.applyScrim && <div className="cinemograph--scrim-component"></div>}
          </div>
        )}
      </div>
    </StyledCinemographHero>
  );
};

export const CinemographHeroConfig = {
  emptyLabel: 'CinemographHero',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/cinemograph-hero/v1/cinemograph-hero`,
};
//TODO:Need to fix the lint issue for editable component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CinemographHeroEditable = (props: any) => (
  <div data-testid="luxury-CinemographHero" data-component-name="m-luxury-CinemographHero">
    <EditableComponent config={CinemographHeroConfig} {...props}>
      <CinemographHero {...props} />
    </EditableComponent>
  </div>
);
